import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCaseStudyResult } from '../../content/placeholders';
import { BlockPrintPattern } from '../brand/Jaipur';
import { ArrowIcon, Eyebrow, focusRing, prefersReducedMotion, useJson } from './shared';
import './home.css';

const PREFERRED_ORDER = ['partywalas', 'junction14', 'senyora', 'burj', 'phylaxisai', 'pakkey-rang'];
const MAX_STORIES = 6;
const ACCENTS = ['text-apn-orange', 'text-apn-pink', 'text-apn-indigo', 'text-apn-saffron'];

const srcOf = (img) => (img && typeof img.src === 'string' && img.src.trim() ? img.src.trim() : null);

function toStory(cs) {
  const slug = String(cs.slug || '').trim().toLowerCase();
  const social = (cs.socialGrid && Array.isArray(cs.socialGrid.images) ? cs.socialGrid.images : []).map(srcOf).filter(Boolean);
  const laptop = srcOf(cs.laptopShowcase);
  const visuals = [...social.slice(0, 4)];
  if (!visuals.length && laptop) visuals.push(laptop);
  const summary = (cs.overview && Array.isArray(cs.overview.paragraphs) ? cs.overview.paragraphs[0] : '') || '';
  return { slug, name: cs.name || slug, logo: cs.logo || null, visuals, summary: summary.trim(), result: getCaseStudyResult(slug) };
}

/** One case study as a vertical story frame, result first. Hovering / focusing flips through its posts. */
function StoryCard({ story, index }) {
  const [frame, setFrame] = useState(0);
  const [active, setActive] = useState(false);
  const [primed, setPrimed] = useState(false);
  const count = story.visuals.length;

  useEffect(() => {
    if (!active || count < 2 || prefersReducedMotion()) return undefined;
    const t = setInterval(() => setFrame((f) => (f + 1) % count), 900);
    return () => clearInterval(t);
  }, [active, count]);

  const shown = active ? frame : 0;
  const start = () => {
    setPrimed(true);
    setActive(true);
  };
  const stop = () => {
    setActive(false);
    setFrame(0);
  };

  return (
    <li className="snap-start shrink-0">
      <Link
        to={`/work/${story.slug}`}
        data-cta="home-work-story"
        onMouseEnter={start}
        onMouseLeave={stop}
        onFocus={start}
        onBlur={stop}
        className={`group relative flex aspect-[9/16] h-[min(34rem,74svh)] short:h-[min(30rem,76svh)] flex-col overflow-hidden rounded-[28px] border border-apn-line bg-apn-surface text-apn-ink transition hover:-translate-y-1 ${focusRing}`}
      >
        {/* Visual */}
        <div className="relative h-[46%] shrink-0 overflow-hidden bg-apn-surface-2">
          {count ? (
            story.visuals.slice(0, primed ? count : 1).map((src, i) => (
              <img
                key={src}
                src={src}
                alt={i === 0 ? `${story.name} social media work by Apnaaapan` : ''}
                loading="lazy"
                decoding="async"
                className={`home-crossfade absolute inset-0 h-full w-full object-cover ${i === shown ? 'opacity-100' : 'opacity-0'}`}
              />
            ))
          ) : (
            <div className={`absolute inset-0 ${ACCENTS[index % ACCENTS.length]}`}>
              <BlockPrintPattern opacity={0.2} />
            </div>
          )}
          {/* Story progress segments */}
          <div aria-hidden="true" className="absolute inset-x-3 top-3 flex gap-1 text-white">
            {Array.from({ length: Math.max(count, 1) }).map((_, i) => (
              <span key={i} className={`home-story-bar flex-1 ${i <= shown ? 'opacity-95' : 'opacity-40'}`} />
            ))}
          </div>
          <div className="absolute left-3 top-6 flex items-center gap-2 rounded-full bg-black/45 py-1 pl-1 pr-3 text-sm font-semibold text-white backdrop-blur-md">
            {story.logo ? (
              <img src={story.logo} alt="" loading="lazy" decoding="async" className="h-7 w-7 rounded-full bg-white object-contain p-0.5" />
            ) : null}
            <span>{story.name}</span>
          </div>
        </div>

        {/* Result first */}
        <div className="flex flex-1 flex-col p-5 sm:p-6">
          <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${ACCENTS[index % ACCENTS.length]}`}>
            {story.result ? `Result · ${story.result.timeframe}` : 'Case study'}
          </p>
          <h3 className="mt-2 font-display text-[1.75rem] font-extrabold leading-[1.02] tracking-[-0.02em] sm:text-[2rem] short:text-[1.6rem]">
            {story.result ? story.result.headline : story.name}
          </h3>
          <p className="mt-2 text-sm text-apn-muted">{story.result ? `for ${story.name}` : story.summary.slice(0, 110)}</p>
          <span className="mt-auto inline-flex items-center gap-2 pt-4 text-base font-semibold text-apn-ink group-hover:text-apn-orange">
            See the case study
            <ArrowIcon className="h-5 w-5 transition group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </li>
  );
}

function SkeletonCard() {
  return (
    <li aria-hidden="true" className="snap-start shrink-0">
      <div className="aspect-[9/16] h-[min(34rem,74svh)] short:h-[min(30rem,76svh)] animate-pulse rounded-[28px] bg-apn-surface-2" />
    </li>
  );
}

export default function WorkStories() {
  const { data, loading } = useJson('/api/case-studies');
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const stories = useMemo(() => {
    const list = data && Array.isArray(data.caseStudies) ? data.caseStudies.filter((cs) => cs && cs.slug) : [];
    const rank = (slug) => {
      const i = PREFERRED_ORDER.indexOf(String(slug).toLowerCase());
      return i === -1 ? PREFERRED_ORDER.length : i;
    };
    return [...list].sort((a, b) => rank(a.slug) - rank(b.slug)).slice(0, MAX_STORIES).map(toStory);
  }, [data]);

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setEdges({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges, stories.length]);

  const scrollByCard = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollByCard(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollByCard(-1);
    }
  };

  const empty = !loading && !stories.length;

  const arrowClass = `hidden h-12 w-12 items-center justify-center rounded-full border-2 border-apn-ink text-apn-ink transition enabled:hover:bg-apn-ink enabled:hover:text-apn-bg disabled:opacity-30 md:inline-flex ${focusRing}`;

  return (
    <section id="work" aria-labelledby="home-work-title" className="scroll-mt-24 overflow-x-clip bg-apn-bg py-14 sm:py-20 short:py-10">
      <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <Eyebrow>The work</Eyebrow>
          <h2 id="home-work-title" data-animate="split-heading" className="mt-3 font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.03em] text-apn-ink sm:text-5xl lg:text-6xl">
            Results first. Then the story.
          </h2>
          <p className="mt-4 text-lg text-apn-text">Swipe through what we built and what it did for the brand.</p>
        </div>
        <div className={empty ? 'hidden' : 'flex gap-3'}>
          <button type="button" className={arrowClass} onClick={() => scrollByCard(-1)} disabled={edges.start} aria-label="Previous case study">
            <ArrowIcon direction="left" />
          </button>
          <button type="button" className={arrowClass} onClick={() => scrollByCard(1)} disabled={edges.end} aria-label="Next case study">
            <ArrowIcon />
          </button>
        </div>
      </div>

      {empty ? null : (
      <ul
        ref={scrollerRef}
        onScroll={updateEdges}
        onKeyDown={onKeyDown}
        aria-label="Case studies"
        aria-busy={loading || undefined}
        className="home-scroller mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-4 px-4 pb-4 sm:scroll-px-6 sm:px-6 lg:scroll-px-[max(2rem,calc((100vw-80rem)/2+2rem))] lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))]"
      >
        {loading ? [0, 1, 2, 3].map((i) => <SkeletonCard key={i} />) : stories.map((s, i) => <StoryCard key={s.slug} story={s} index={i} />)}
      </ul>
      )}

      <div className="mx-auto mt-6 max-w-7xl px-4 sm:px-6 lg:px-8">
        <Link to="/work" data-cta="home-work-all" className={`inline-flex min-h-[44px] items-center gap-2 font-semibold text-apn-ink underline decoration-apn-orange decoration-2 underline-offset-4 hover:text-apn-orange ${focusRing} rounded`}>
          All our work
          <ArrowIcon className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
